import React from 'react';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import { useTranslation } from 'next-i18next';
import { useSelector } from 'react-redux';
import { formatDateWithWeekday } from '@root/src/utils/dates';
import { textP } from '@features/ui/styles/textStyles';
import Banner from '@features/ui/components/Banner';
import BannerLogin from '@features/users/components/BannerLogin';
import SectionContent from '@features/ui/components/SectionContent';
import SectionTitle from '@features/ui/components/SectionTitle';
import { selectIsAuthenticated } from '@features/users/slices/userSlice';
import SerieSubscriptionPackSelect from '@features/serieDetail/components/SerieSubscriptionPackSelect';
import EventAndCategoryList from '@features/eventDetail/components/EventCategoryList/EventAndCategoryList';
import { selectEventsWithOpenRegCategoriesCount } from '@features/eventDetail/slices/eventDetailServerSlice';
import { selectSerieDetail, selectSeriePacks, selectSerieRegistrationsOpen } from '@features/serieDetail/slices/serieDetailServerSlice';
import SerieSubscriptionForSelect from '@features/serieDetail/components/SerieSubscriptionForSelect';
import SerieSubscriptionCartButton from '@features/serieDetail/components/SerieSubscriptionCartButton';

const SerieSubscriptions = () => {
  const { t } = useTranslation();
  const router = useRouter();

  const { name, status } = useSelector(selectSerieDetail);
  const packs = useSelector(selectSeriePacks);
  const registrationsOpen = useSelector(selectSerieRegistrationsOpen);
  const logged = useSelector(selectIsAuthenticated);
  const categoriesCount = useSelector(selectEventsWithOpenRegCategoriesCount);

  const showLoginBanner = !logged && registrationsOpen;
  const showDiscountBanner = registrationsOpen && packs.some(({ reduction }) => reduction > Number.EPSILON);
  const showSelectors = logged && registrationsOpen;
  const showBottomCartButton = logged && registrationsOpen && categoriesCount > 0;

  // TODO: improve sub intro sentence (at least at "subscription will open on XXX")
  return (
    <SectionContent>
      <SectionTitle>
        {t('serieSubscriptions_title.message')}
      </SectionTitle>

      <SubscriptionsIntro>
        {registrationsOpen
          ? t('serieSubscription_serieRegOpenIntro.message', {
            startDate: formatDateWithWeekday(status.registrationFrom, router.locale),
            endDate: formatDateWithWeekday(status.registrationTo, router.locale),
          })
          : t('serieSubscription_serieRegCloseIntro.message')}
      </SubscriptionsIntro>

      {showLoginBanner && <BannerLogin />}

      {showSelectors && <SerieSubscriptionForSelect />}

      {showDiscountBanner && (
        <StyledBanner>
          {t('serieSubscription_bannerDiscounts.message', { serieName: name })}
        </StyledBanner>
      )}

      {showSelectors && <SerieSubscriptionPackSelect />}

      <EventAndCategoryList />

      {showBottomCartButton && (
        <ButtonContainer>
          <SerieSubscriptionCartButton />
        </ButtonContainer>
      )}
    </SectionContent>
  );
};

export default SerieSubscriptions;

const StyledBanner = styled(Banner)`
  margin-top: 20px;
  margin-bottom: 20px;
  width: 100%;
  text-align: center;
  ${textP};
`;

const SubscriptionsIntro = styled.p`
  ${textP};
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;
